"use client";

import * as React from "react";
import { Ear, Moon, Activity, Wind, Info, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
const symptoms = [{
  title: "Cardiovascular Strain",
  icon: Activity,
  tip: "Repeated oxygen drops force your heart to work harder, significantly increasing risks of chronic hypertension and heart failure.",
  link: "/HealthRisksPage"
}, {
  title: "Increased Stroke Risk",
  icon: Wind,
  tip: "The sudden changes in blood pressure and reduced oxygen flow to the brain during apnea events are major contributors to stroke.",
  link: "/HealthRisksPage"
}, {
  title: "Type 2 Diabetes",
  icon: Moon,
  tip: "Sleep fragmentation disrupts metabolic health, often leading to insulin resistance and making blood sugar regulation difficult.",
  link: "/HealthRisksPage"
}, {
  title: "Cognitive Decline",
  icon: Ear,
  tip: "Chronic lack of restorative sleep impairs the brain's ability to clear toxins, potentially leading to long-term neurological issues.",
  link: "/HealthRisksPage"
}];
export default function IsItTimeToSeeASpecialist() {
  return <section data-section-id="127225" id="untreated-osa-dangers" className="relative py-24 overflow-hidden bg-gradient-to-b from-background to-secondary/40">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[30rem] h-[30rem] bg-red-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
            The Dangers of Untreated OSA
          </h2>
          <p className="text-lg text-muted-foreground">
            Sleep apnea is a progressive condition. Ignoring the warning signs can lead to severe systemic health complications that affect your entire body.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {symptoms.map((item, i) => <Card key={i} data-index={i} className="relative flex flex-col h-full overflow-hidden border-border/60 bg-white/80 backdrop-blur-md shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-[24px]">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center text-red-600 mb-4 shadow-sm">
                  <item.icon className="w-7 h-7" strokeWidth={2} />
                </div>
                <CardTitle className="text-xl font-semibold text-foreground">
                  {item.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="flex-grow pb-6">
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full h-auto">
                      <Info className="w-4 h-4 text-red-600" />
                      Medical Insight
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent side="top" align="start" className="w-72 text-sm leading-relaxed bg-white/98 backdrop-blur-xl border-red-100 shadow-2xl rounded-2xl p-5 text-muted-foreground">
                    <strong className="block text-foreground mb-1 font-semibold">
                      Why this happens:
                    </strong>
                    {item.tip}
                  </HoverCardContent>
                </HoverCard>
              </CardContent>

              <CardFooter className="pt-0 mt-auto">
                <Button variant="ghost" className="w-full justify-between group hover:bg-primary/5 hover:text-primary text-muted-foreground font-medium" asChild>
                  <Link to={item.link}>
                    View Health Risks
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}